import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { TextField, Select } from '@ui/inputs';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { SpecEditorContext } from './SpecEditorContext';
import {
  SCORE_NAME_REGEX,
  RESERVED_SCORE_NAME_REGEX,
  SCHEMA_ALLOWED_KEYS,
  FEED_NAME_REGEX,
  FEED_NAME_SEPARATOR,
} from './specSchema';
import { matchCount, skeletonPaths } from './jsonPath';
import { PROSE_LEGACY_NOTE, isProseRetired } from './proseContract';
import { feedNamesFor } from './summary';
import { Consequence, MicroLabel, ReadOnlyJson, Row } from './styles';

// Question 4 — what comes out.
//
// The one-vs-several choice is a RADIO that owns which key serialises. `score_jsonpath` and
// `scores` are mutually exclusive in the loader, and setting neither is the quietest trap in the
// current editor: an empty `output_extraction` saves cleanly and can never lock. Here the radio is
// simply unanswered until you answer it, and the Problems strip says why that matters.
//
// Score names are permanent. They become the `factor_score_values.score_name`, the published feed
// suffix, and S3/NATS tokens — so the charset, the uniqueness and the reserved `<digits>runs`
// collision are checked as typed rather than at lock.
//
// `extra_columns` is deliberately styled and worded apart from scores because its semantics are the
// OPPOSITE: a miss is recorded as null and nothing fails, whereas a score that misses fails the
// whole document and a null score fails it too.
//
// The OUTPUT CONTRACT is a second radio, for the same reason as the first: since PR #455 create and
// PATCH 422 a spec that declares both `output_extraction.output_schema` and
// `score_gen.format_prompt`, and 422 one that declares neither. A radio that owns which key
// serialises makes both states unreachable from the form. It is here, in question 4, even though
// one of the two keys lives in question 3 — this is the panel about what comes out, and the Studio
// prints the other end of the pointer.
//
// The radio tracks key PRESENCE, not truthiness. Choosing prose writes an empty `format_prompt` so
// the choice sticks while the operator goes to question 3 to write it; the validator still counts a
// blank as absent, exactly as the server does, so "declare an output contract" keeps firing until
// there is really something there.

const PathRow = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(140px, 1fr) minmax(180px, 2fr) auto;
  align-items: start;
  padding: 10px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

// The score rows carry a fourth control (feed_name) that the extra-column rows do not.
const ScoreRow = styled(PathRow)`
  grid-template-columns: minmax(120px, 1fr) minmax(160px, 2fr) minmax(140px, 1fr) auto;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

// `feed_name` is checked here rather than only in the Problems strip because its format lives in the
// engine's STRICT tier: a bad value saves without complaint and then fails at preview or lock.
const feedNameProblem = (value) => {
  if (!value) return null;
  if (!FEED_NAME_REGEX.test(value)) return 'Lowercase letters, digits and underscores only — the suffix alone.';
  if (FEED_NAME_SEPARATOR.test(value)) return '`__` is the separator a derived multi-score name uses.';
  if (RESERVED_SCORE_NAME_REGEX.test(value)) return 'Reserved — names like “3runs” collide with the multi-run suffix.';
  return null;
};

const coherenceHint = (expr, schema, exactlyOne) => {
  if (!schema) return 'No output_schema declared, so this path cannot be checked here.';
  if (!expr) return null;
  const { supported, matches } = matchCount(expr, schema);
  if (!supported) return 'Outside the path forms this editor can evaluate — the compiler will check it.';
  if (exactlyOne) {
    if (matches === 1) return 'Resolves to exactly one node of the declared shape.';
    return `Resolves to ${matches} nodes. A score path must resolve to exactly one, or extraction fails on every document.`;
  }
  if (matches >= 1) return 'Resolves against the declared shape.';
  return 'Resolves to nothing in the declared shape, so it would be null on every run.';
};

const isBadCoherence = (expr, schema, exactlyOne) => {
  if (!schema || !expr) return false;
  const { supported, matches } = matchCount(expr, schema);
  if (!supported) return false;
  return exactlyOne ? matches !== 1 : matches < 1;
};

export const OutPanel = () => {
  const { definition, setValue, removeValue } = React.useContext(SpecEditorContext);

  const output = (definition || {}).output_extraction || {};
  const schema = output.output_schema && typeof output.output_schema === 'object' ? output.output_schema : null;
  const scores = Array.isArray(output.scores) ? output.scores : null;
  // `score_jsonpath` is no longer a mode the operator can choose — it is a legacy key to migrate off.
  const hasLegacyShorthand = output.score_jsonpath !== undefined && output.score_jsonpath !== null;

  const pickable = React.useMemo(() => (schema ? skeletonPaths(schema) : []), [schema]);
  const pathOptions = pickable.map((p) => ({ value: p, label: p }));

  // The cardinality radio is gone: `score_jsonpath` is rejected on write (create and PATCH 422 any
  // body containing it), so there is no longer a choice to offer — every spec declares a `scores`
  // list, of one entry or several. What is left is the one-way migration off the shorthand.
  //
  // The rewrite is fully determined: one entry named `score`, which is exactly what the loader
  // desugars the shorthand into, and the name that keeps the feed's bare `score_spec_<id>` form. It
  // is still a button rather than something done on load — the operator sees what changed, and this
  // editor does not rewrite a spec behind them (the same rule UnknownRootKeysStrip follows).
  const rewriteShorthand = () => {
    setValue('output_extraction.scores', [{ name: 'score', jsonpath: output.score_jsonpath || '' }]);
    removeValue('output_extraction.score_jsonpath');
  };
  const seedScores = () => setValue('output_extraction.scores', [{ name: '', jsonpath: '' }]);

  // Presence, not truthiness — see the header. `output_schema: null` is how the API spells "absent",
  // so it is not a declaration either.
  const schemaDeclared = output.output_schema !== undefined && output.output_schema !== null;
  const scoreGen = (definition || {}).score_gen || {};
  const proseDeclared = scoreGen.format_prompt !== undefined && scoreGen.format_prompt !== null;
  const contract =
    schemaDeclared && proseDeclared ? 'both' : schemaDeclared ? 'schema' : proseDeclared ? 'prose' : 'none';

  // Minimal strict-mode object, so picking "structured outputs" produces a contract that is actually
  // declared rather than an empty section that still 422s. `$.Score` is the path every shipped
  // single-score spec uses, so the score path above usually resolves against it immediately.
  const chooseSchemaContract = () => {
    removeValue('score_gen.format_prompt');
    if (!schemaDeclared) {
      setValue('output_extraction.output_schema', {
        type: 'object',
        properties: { Score: { type: 'number' } },
        required: ['Score'],
        additionalProperties: false,
      });
    }
  };

  // Prose is no longer authorable, so there is no "choose prose" any more. A spec that already has one
  // keeps it — the loader is tolerant, and rewriting the output contract of a running spec is not
  // something this editor does on the operator's behalf.

  const specExtras = output.extra_columns && typeof output.extra_columns === 'object' ? output.extra_columns : {};
  const specExtraNames = Object.keys(specExtras);
  // Every score is a possible SCOPE for a column. Only a NAMED score can be offered as one — the
  // picker has nothing to call a nameless row, and the column would land under a score the loader
  // is going to reject anyway.
  const scoreScopes = (scores || []).map((score, index) => ({
    index,
    name: (score && score.name) || '',
    columns: score && typeof score.extra_columns === 'object' && score.extra_columns ? score.extra_columns : {},
  }));
  const namedScopes = scoreScopes.filter((scope) => scope.name);
  const feeds = feedNamesFor(definition);

  // What this score WOULD publish with no override, so the placeholder shows what the field
  // replaces rather than an abstract example.
  const derivedFeedNameFor = (index) => {
    const id = (definition || {}).score_spec_id;
    if (!id) return 'acme_conviction';
    if (!scores || scores.length === 1) return `${id}`;
    const score = scores[index];
    return `${id}__${(score && score.name) || 'unnamed'}`;
  };

  const nameProblem = (name, index) => {
    if (!name) return 'Required — it becomes the feed suffix and is permanent at lock.';
    if (!SCORE_NAME_REGEX.test(name)) return 'Lowercase letters, digits and underscores only.';
    if (RESERVED_SCORE_NAME_REGEX.test(name))
      return 'Reserved — names like “3runs” collide with the multi-run feed suffix.';
    const duplicate = (scores || []).some((s, i) => i !== index && s && s.name === name);
    if (duplicate) return 'Already used by another score in this spec.';
    return null;
  };

  return (
    <div>
      <MicroLabel>The scores this spec extracts</MicroLabel>

      {hasLegacyShorthand && (
        <Container top={16}>
          <TextField
            label="score_jsonpath (legacy)"
            mono
            readOnly
            value={output.score_jsonpath || ''}
            hint="Read-only. This key cannot be authored any more."
          />
          <Container top={8}>
            <Consequence $tone="bad">
              Create and PATCH reject any body containing `score_jsonpath`, so this draft cannot be saved while it is
              here. The loader still accepts it, so the spec as STORED runs fine — this is a write-door rule, not a
              broken spec.
            </Consequence>
          </Container>
          <Container top={8}>
            <SkeletonButton onClick={rewriteShorthand}>Rewrite as a `scores` list</SkeletonButton>
          </Container>
          <Container top={8}>
            <Consequence>
              One entry named “score”, which is what the loader desugars this into — so the published feed keeps its
              bare `score_spec_&lt;id&gt;` name.
            </Consequence>
          </Container>
        </Container>
      )}

      {!scores && !hasLegacyShorthand && (
        <Container top={16}>
          <Consequence $tone="bad">
            Unanswered. A spec with no `scores` list saves cleanly and can never lock.
          </Consequence>
          <Container top={8}>
            <FlatButton onClick={seedScores}>Add a score</FlatButton>
          </Container>
        </Container>
      )}

      {scores && (
        <Container top={16}>
          {scores.map((score, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ScoreRow key={`score-${index}`}>
              <TextField
                label={index === 0 ? 'Name' : undefined}
                value={(score && score.name) || ''}
                onChange={(v) => setValue(`output_extraction.scores.${index}.name`, v)}
                invalid={!!nameProblem((score && score.name) || '', index)}
                hint={nameProblem((score && score.name) || '', index)}
              />
              {/* Pick-from-shape used to sit on the single-score control alone. It belongs on every
                  score: a path picked out of the declared shape cannot be one that resolves to
                  nothing. It goes UNDER the field rather than in a fifth column so the row stays
                  readable, and it is labelled per score — two rows sharing one accessible name is
                  no label at all. */}
              <div>
                <TextField
                  label={index === 0 ? 'jsonpath' : undefined}
                  mono
                  value={(score && score.jsonpath) || ''}
                  onChange={(v) => setValue(`output_extraction.scores.${index}.jsonpath`, v)}
                  invalid={isBadCoherence(score && score.jsonpath, schema, true)}
                  hint={coherenceHint(score && score.jsonpath, schema, true)}
                />
                {pathOptions.length > 0 && (
                  <Container top={8}>
                    <Select
                      aria-label={`Pick a path from the declared shape for ${(score && score.name) || 'this score'}`}
                      value=""
                      placeholder="Pick from the declared shape…"
                      options={pathOptions}
                      onChange={(v) => v && setValue(`output_extraction.scores.${index}.jsonpath`, v)}
                    />
                  </Container>
                )}
              </div>
              <TextField
                label={index === 0 ? 'feed_name (optional)' : undefined}
                mono
                value={(score && score.feed_name) || ''}
                onChange={(v) =>
                  v
                    ? setValue(`output_extraction.scores.${index}.feed_name`, v)
                    : removeValue(`output_extraction.scores.${index}.feed_name`)
                }
                placeholder={derivedFeedNameFor(index)}
                invalid={!!feedNameProblem((score && score.feed_name) || '')}
                hint={
                  feedNameProblem((score && score.feed_name) || '') ||
                  (score && score.feed_name
                    ? `Publishes score_spec_${score.feed_name} instead of the derived name. Reusing a name an approved spec already publishes is a 409 at approve.`
                    : 'Leave empty to publish under the derived name. Set it to keep an existing feed when re-cutting a version.')
                }
              />
              <Container top={index === 0 ? 22 : 0}>
                <TransparentFlatButton onClick={() => removeValue(`output_extraction.scores.${index}`)}>
                  Remove
                </TransparentFlatButton>
              </Container>
            </ScoreRow>
          ))}
          <Container top={16}>
            <FlatButton
              onClick={() => setValue(`output_extraction.scores.${scores.length}`, { name: '', jsonpath: '' })}
            >
              Add a score
            </FlatButton>
          </Container>
          {scores.length > 1 && !schema && (
            <Container top={16}>
              <Consequence $tone="bad">
                More than one score requires an `output_schema`. Multi-score extraction is all-or-nothing, so the shape
                has to be guaranteed rather than asked for in prose.
              </Consequence>
            </Container>
          )}
          {scores.length === 1 && (
            <Container top={16}>
              <Consequence>
                One declared score, so the feed keeps its bare `score_spec_&lt;id&gt;` name. Feed naming keys on the
                declared COUNT, not on the score’s name.
              </Consequence>
            </Container>
          )}
          {scores.length > 1 && (
            <Container top={16}>
              <Consequence $tone="warn">
                Declaring more than one score renames every feed this spec publishes to `…__&lt;name&gt;`, unless a
                score sets its own feed_name. That naming is permanent at lock.
              </Consequence>
            </Container>
          )}
        </Container>
      )}

      <Container top={24}>
        <MicroLabel>Extra columns</MicroLabel>
        <Container top={6}>
          <Consequence>
            Best-effort, and the opposite of scores: a path that matches nothing is recorded as null and the document
            still scores. A score that misses fails the document outright.
          </Consequence>
        </Container>
        <Container top={6}>
          <Consequence>
            A spec-level column is BROADCAST — it appears on every feed this spec publishes. A per-score column is
            scoped to one score’s feed. The same name cannot be used at both levels.
          </Consequence>
        </Container>
        <Container top={16}>
          {specExtraNames.map((name) => (
            <PathRow key={`extra-${name}`}>
              <TextField label="Column" value={name} readOnly hint="Every score’s feed" />
              <TextField
                label="jsonpath"
                mono
                value={specExtras[name]}
                onChange={(v) => setValue(['output_extraction', 'extra_columns', name], v)}
                invalid={isBadCoherence(specExtras[name], schema, false)}
                hint={coherenceHint(specExtras[name], schema, false)}
              />
              <Container top={24}>
                <TransparentFlatButton onClick={() => removeValue(['output_extraction', 'extra_columns', name])}>
                  Remove
                </TransparentFlatButton>
              </Container>
            </PathRow>
          ))}

          {scoreScopes.map((scope) =>
            Object.keys(scope.columns).map((name) => (
              <PathRow key={`extra-${scope.index}-${name}`}>
                <TextField label="Column" value={name} readOnly hint={`${scope.name || 'unnamed score'} only`} />
                <TextField
                  label="jsonpath"
                  mono
                  value={scope.columns[name]}
                  onChange={(v) => setValue(['output_extraction', 'scores', scope.index, 'extra_columns', name], v)}
                  invalid={isBadCoherence(scope.columns[name], schema, false)}
                  hint={coherenceHint(scope.columns[name], schema, false)}
                />
                <Container top={24}>
                  <TransparentFlatButton
                    onClick={() => removeValue(['output_extraction', 'scores', scope.index, 'extra_columns', name])}
                  >
                    Remove
                  </TransparentFlatButton>
                </Container>
              </PathRow>
            ))
          )}

          <Container top={16}>
            <AddExtraColumn
              specNames={specExtraNames}
              scopes={namedScopes}
              onAdd={(name, scopeIndex) =>
                scopeIndex === null
                  ? setValue(['output_extraction', 'extra_columns', name], '')
                  : setValue(['output_extraction', 'scores', scopeIndex, 'extra_columns', name], '')
              }
            />
          </Container>
        </Container>
      </Container>

      <Container top={24}>
        <MicroLabel>How the output shape is declared</MicroLabel>
        <Container top={6}>
          <Consequence>
            An `output_schema`. It is enforced — the scoring call carries strict structured outputs, so the model cannot
            emit malformed JSON — where a prose `format_prompt` is only followed best-effort. A spec declares one of
            them, and prose is no longer authorable.
          </Consequence>
        </Container>

        {contract === 'prose' && (
          <Container top={16}>
            <Consequence $tone={isProseRetired() ? 'bad' : 'warn'}>{PROSE_LEGACY_NOTE}</Consequence>
            <Container top={6}>
              <Body7 isLowContrast>
                The prose itself is in question 3. Nothing here rewrites it: the loader accepts it, the spec runs, and
                changing the output contract of a working spec is a decision with a preview behind it, not a button.
              </Body7>
            </Container>
          </Container>
        )}

        {contract === 'both' && (
          <Container top={8}>
            <Consequence $tone="bad">
              Both are declared. The loader tolerates that — which is why specs like this exist and still run — but
              create and PATCH now reject it, so this draft cannot be saved until the prose goes. Nothing is deleted
              until you press.
            </Consequence>
            <Container top={8}>
              <SkeletonButton onClick={chooseSchemaContract}>Drop format_prompt, keep the schema</SkeletonButton>
            </Container>
            <Container top={6}>
              <Body7 isLowContrast>
                Move anything the schema cannot express — a numeric range, a rubric — into the prompt in question 3
                first. There is no longer a "keep the prose" option: the schema is the contract.
              </Body7>
            </Container>
          </Container>
        )}

        {contract === 'none' && (
          <Container top={8}>
            <Consequence $tone="bad">Unanswered. A spec with no output contract is rejected on save.</Consequence>
            <Container top={8}>
              <SkeletonButton onClick={chooseSchemaContract}>Declare an output_schema</SkeletonButton>
            </Container>
          </Container>
        )}

        <Container top={16}>
          {schema ? (
            <>
              <ReadOnlyJson>{JSON.stringify(schema, null, 2)}</ReadOnlyJson>
              <Container top={6}>
                <Consequence>
                  {`Edit the shape in this section’s { } view. Strict mode allows only ${SCHEMA_ALLOWED_KEYS.join(
                    ', '
                  )} — every object must list all of its properties in \`required\` and set \`additionalProperties: false\`.`}
                </Consequence>
              </Container>
            </>
          ) : (
            <Consequence $tone={contract === 'prose' ? 'warn' : undefined}>
              {contract === 'prose'
                ? 'Prose contract: the model is asked for a shape in words and may not produce it — which is the malformed-JSON re-score tax. No score path above can be checked before lock either. Prose is the fallback for a model without structured-output support; prefer a schema where the model has one.'
                : 'No output_schema declared.'}
            </Consequence>
          )}
        </Container>
      </Container>

      <Container top={24}>
        <MicroLabel>Feeds this spec publishes</MicroLabel>
        <Container top={6}>
          {feeds.length === 0 ? (
            <Body7 isLowContrast>Set a spec id to see the feed names.</Body7>
          ) : (
            feeds.map((feed) => (
              <Body7 key={feed} isLowContrast>
                {feed}
              </Body7>
            ))
          )}
        </Container>
      </Container>
    </div>
  );
};

// Spec-level is the default scope: it is what every shipped spec uses, and it is the answer to
// "show this on the feed" without further thought. The per-score scope is opt-in.
const SPEC_SCOPE = '__spec__';

const AddExtraColumn = ({ specNames, scopes, onAdd }) => {
  const [name, setName] = React.useState('');
  const [scopeValue, setScopeValue] = React.useState(SPEC_SCOPE);

  const scope = scopes.find((s) => String(s.index) === String(scopeValue)) || null;
  const isSpecLevel = scopeValue === SPEC_SCOPE || !scope;
  const taken = isSpecLevel ? specNames.indexOf(name) > -1 : Object.keys(scope.columns).indexOf(name) > -1;

  // Shadowing is a 422 at create/PATCH, and it is symmetric: a per-score key may not shadow a
  // spec-level one, so adding either half of the pair second is the same mistake. Caught here rather
  // than on the round trip, because the round trip rejects the WHOLE save.
  const shadowedScores = isSpecLevel && name ? scopes.filter((s) => Object.keys(s.columns).indexOf(name) > -1) : [];
  const shadowsSpecLevel = !isSpecLevel && !!name && specNames.indexOf(name) > -1;
  const collides = shadowedScores.length > 0 || shadowsSpecLevel;
  // Deliberately NOT the loader's score-name rule, and deliberately not a block. `_SCORE_NAME_RE`
  // (specs_loader.py:218) is bound to `_ScoreEntry.name` alone, because a score name becomes an S3
  // key component and a NATS subject token. An extra column is only a JSONB key in
  // `factor_scores.signal_payload` — `dict[StrictStr, StrictStr]` with no validator at all
  // (specs_loader.py:267) — so `Thinking` is legal, and blocking it forced an operator into the raw
  // pane to mirror the model's own PascalCase key. The charset survives as house advice because the
  // convention is real: all 27 distinct extra_columns keys across the shipped specs are snake_case,
  // and the key becomes a CSV/parquet column name in snapshot.build_frames.
  const unconventional = !!name && !/^[a-z0-9_]+$/.test(name);

  const hint = () => {
    if (taken) return 'That column already exists in this scope — adding it again would overwrite its path.';
    if (shadowsSpecLevel)
      return 'The spec already declares that column for every score. A per-score column may not shadow a spec-level one — rename one of them.';
    if (shadowedScores.length > 0)
      return `${shadowedScores
        .map((s) => s.name)
        .join(
          ', '
        )} already declares that column. A per-score column may not shadow a spec-level one — rename one of them.`;
    if (unconventional)
      return 'The loader accepts any key. Every shipped spec uses lowercase letters, digits and underscores, which keeps it a usable column name downstream.';
    return isSpecLevel
      ? 'Stored in the parent score row’s signal_payload, so it appears on every feed this spec publishes.'
      : `Stored in ${scope.name}’s own row, so it appears on that score’s feed only.`;
  };

  return (
    <Row $align="flex-start">
      <TextField
        label="Add a column"
        value={name}
        onChange={setName}
        placeholder="thinking"
        invalid={taken || collides}
        hint={hint()}
      />
      {scopes.length > 0 && (
        <Select
          label="Which feeds see it"
          value={scopeValue}
          onChange={setScopeValue}
          options={[{ value: SPEC_SCOPE, label: 'Every score’s feed (spec-level)' }].concat(
            scopes.map((s) => ({ value: String(s.index), label: `${s.name} only (per-score)` }))
          )}
        />
      )}
      <Container top={24}>
        <FlatButton
          onClick={() => {
            if (!name || taken || collides) return;
            onAdd(name, isSpecLevel ? null : scope.index);
            setName('');
          }}
        >
          Add
        </FlatButton>
      </Container>
    </Row>
  );
};
